import type { RefCallback } from "react";
import type { CmsPage } from "../../../../types/cms";
import { pagePath, sectionPath } from "../../pageAddress";
import type { PageSection } from "../../pageSection";
import type { CmsPagesStatus } from "../../useCmsPages";
import styles from "./PageAddressFields.module.css";

type ParentPageFieldProps = {
  id: string;
  section: PageSection;
  pages: CmsPage[];
  pagesStatus: CmsPagesStatus;
  value: string;
  savedParentPath: string | null;
  onChange: (parentId: string) => void;
  inputRef?: RefCallback<HTMLElement>;
};

function statusHint(pagesStatus: CmsPagesStatus): string {
  if (pagesStatus === "loading") {
    return "Loading pages…";
  }
  if (pagesStatus === "failed") {
    return "Pages couldn't be loaded. Reload to change the parent.";
  }
  return "The page this one sits under. Its address comes before this page's slug.";
}

export function ParentPageField({
  id,
  section,
  pages,
  pagesStatus,
  value,
  savedParentPath,
  onChange,
  inputRef,
}: ParentPageFieldProps) {
  const hintId = `${id}-hint`;
  const valueListed = pages.some(page => page.id === value);

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        Parent page
      </label>
      <select
        ref={inputRef}
        id={id}
        className={styles.select}
        value={value}
        onChange={event => onChange(event.target.value)}
        aria-describedby={hintId}
        disabled={pagesStatus === "failed"}
      >
        <option value="">None (top level)</option>
        {value && !valueListed && (
          <option value={value}>
            {savedParentPath ? sectionPath(section, savedParentPath) : "Current parent page"}
          </option>
        )}
        {pages.map(page => (
          <option key={page.id} value={page.id}>
            {page.title} ({pagePath(page)})
          </option>
        ))}
      </select>
      <span id={hintId} className={styles.hint}>
        {statusHint(pagesStatus)}
      </span>
    </div>
  );
}
